import { useQuery } from "@tanstack/react-query";
import {
  buildPermissionsRequest,
  buildRoundPermissionsRequest,
  buildTitlePermissionsRequest,
  type Permissions,
  transformPermissionsResponse,
} from "@/lib/auth/permissions";
import type { AuthzPermissionsData } from "@/usersAndProjects-client";
import { authzPermissions } from "@/usersAndProjects-client";

/**
 * Result type for usePermissions hook
 */
export type UsePermissionsResult = {
  /** Permissions map (action -> boolean), undefined while loading */
  permissions: Permissions | undefined;
  /** Whether the permissions are currently being fetched */
  isLoading: boolean;
  /** Error if the fetch failed */
  error: Error | null;
  /** Function to manually refetch permissions */
  refetch: () => void;
};

/**
 * Hook to fetch global permissions (overview page, create page).
 * Requests GLOBAL_ACTIONS permissions.
 * Permissions are always fetched fresh (no caching).
 *
 * @param options - Optional configuration
 * @param options.enabled - Whether to enable the query. Defaults to true.
 *
 * @returns Permissions data, loading state, error, and refetch function
 *
 * @example
 * const { permissions, isLoading } = useGlobalPermissions();
 * if (permissions?.access_overview) {
 *   // User can access proofing flow
 * }
 */
export function useGlobalPermissions(options?: { enabled?: boolean }): UsePermissionsResult {
  const queryKey = ["permissions", "global"];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = buildPermissionsRequest();

      const response = await authzPermissions({
        body: requestBody as AuthzPermissionsData["body"],
      });

      return transformPermissionsResponse(
        response.data as Parameters<typeof transformPermissionsResponse>[0],
      );
    },
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch project-specific permissions (title side sheet).
 * Requests TITLE_ACTIONS permissions (access_side_sheet, review_pdf, close_proofing_round, etc).
 * Permissions are always fetched fresh (no caching).
 *
 * @param projectId - Project ID (required)
 * @param options - Optional configuration
 * @param options.enabled - Whether to enable the query. Defaults to true.
 *
 * @returns Permissions data, loading state, error, and refetch function
 *
 * @example
 * const { permissions, isLoading } = useProjectPermissions(projectId);
 * if (permissions?.edit_metadata) {
 *   // User can edit metadata for this project
 * }
 */
export function useProjectPermissions(
  projectId: string,
  options?: { enabled?: boolean },
): UsePermissionsResult {
  const queryKey = ["permissions", "project", projectId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = buildTitlePermissionsRequest(projectId);

      const response = await authzPermissions({
        body: requestBody as AuthzPermissionsData["body"],
      });

      return transformPermissionsResponse(
        response.data as Parameters<typeof transformPermissionsResponse>[0],
      );
    },
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to fetch round-specific permissions (document review page).
 * Requests ROUND_SPECIFIC_ACTIONS permissions (create_annotations, etc).
 * Permissions are always fetched fresh (no caching).
 *
 * @param projectId - Project ID (required)
 * @param roundState - Current round state (required, e.g., "reviewing", "approving")
 * @param options - Optional configuration
 * @param options.enabled - Whether to enable the query. Defaults to true.
 *
 * @returns Permissions data, loading state, error, and refetch function
 *
 * @example
 * const { permissions } = useRoundPermissions(projectId, round.state);
 * if (permissions?.create_annotations) {
 *   // User can create annotations in this round
 * }
 */
export function useRoundPermissions(
  projectId: string,
  roundState: string,
  options?: { enabled?: boolean },
): UsePermissionsResult {
  const queryKey = ["permissions", "project", projectId, "round", roundState];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const requestBody = buildRoundPermissionsRequest(projectId, roundState);

      const response = await authzPermissions({
        body: requestBody as AuthzPermissionsData["body"],
      });

      return transformPermissionsResponse(
        response.data as Parameters<typeof transformPermissionsResponse>[0],
      );
    },
    enabled: options?.enabled ?? true,
    staleTime: 0,
    gcTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * @deprecated Use useGlobalPermissions, useProjectPermissions, or useRoundPermissions instead
 */
export function usePermissions(
  projectId?: string,
  options?: { enabled?: boolean; roundState?: string },
): UsePermissionsResult {
  // Build cache key based on projectId and roundState
  const queryKey = projectId
    ? options?.roundState
      ? ["permissions", "project", projectId, "round", options.roundState]
      : ["permissions", "project", projectId]
    : ["permissions", "global"];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Build the permissions request using the appropriate utility
      const requestBody =
        options?.roundState && projectId
          ? buildRoundPermissionsRequest(projectId, options.roundState)
          : projectId
            ? buildTitlePermissionsRequest(projectId)
            : buildPermissionsRequest();

      // Use the generated client instead of manual fetch
      const response = await authzPermissions({
        body: requestBody as AuthzPermissionsData["body"],
      });

      // Transform the response to a simple boolean map
      // The generated client wraps the response in a 'data' property
      // Cast to PermissionsResponse since the generated types have optional fields
      return transformPermissionsResponse(
        response.data as Parameters<typeof transformPermissionsResponse>[0],
      );
    },
    enabled: options?.enabled ?? true, // Allow disabling the query
    staleTime: 0, // Always consider data stale - fetch fresh every time
    gcTime: 0, // Don't cache data
    retry: 1, // Retry failed requests once
    refetchOnWindowFocus: false, // Don't refetch on tab focus
  });

  return {
    permissions: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
